import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

/**
 * ScopeSelector — segmented "This slide" / "All slides" control with
 * inline confirmation when entering global mode with diverse values,
 * and a persistent status bar while in global mode.
 *
 * Supports two visual variants:
 *   - "segmented" (default): pill-style segmented control
 *   - "led": LED indicator radio buttons (instrument panel style)
 *
 * Scope is PERSISTENT — it stays in "All slides" mode until the user
 * explicitly clicks "This slide". Switching slides does NOT reset it.
 *
 * Flow when diversity exists:
 *   1. User clicks "All slides" → pending confirmation shown inline
 *   2. User clicks "Apply to all" → onConfirmApply() fires, scope switches to 'all'
 *   3. User clicks "Cancel" → pending dismissed, stays on 'slide'
 *
 * Flow when NO diversity:
 *   Clicking "All slides" switches immediately (no confirmation needed).
 *
 * @param {Object}   props
 * @param {'slide'|'all'}  props.scope           — Current scope mode
 * @param {Function} props.onScopeChange         — (scope: 'slide'|'all') => void
 * @param {number}   [props.uniqueCount]         — Unique values across slides (for warning)
 * @param {string}   [props.diversityNoun]       — Noun for text (e.g., "templates", "fonts")
 * @param {string}   [props.confirmMessage]      — Inline confirmation text
 * @param {Function} [props.onConfirmApply]      — Called when user confirms
 * @param {'segmented'|'led'} [props.variant]    — Visual variant
 */
export function ScopeSelector({
  scope,
  onScopeChange,
  uniqueCount = 1,
  diversityNoun = 'values',
  confirmMessage,
  onConfirmApply,
  variant = 'segmented',
}) {
  const hasDiversity = uniqueCount > 1;
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const [statusDismissed, setStatusDismissed] = useState(false);

  // Reset pending state when scope changes externally
  useEffect(() => {
    if (scope === 'slide') setPendingConfirm(false);
  }, [scope]);

  const handleAllClick = () => {
    if (scope === 'all') return; // already in global mode
    if (hasDiversity) {
      // Show inline confirmation instead of switching immediately
      setPendingConfirm(true);
    } else {
      // No diversity — safe to switch directly
      onScopeChange('all');
    }
  };

  const handleConfirm = () => {
    setPendingConfirm(false);
    if (onConfirmApply) onConfirmApply(); // apply current value to all slides
    onScopeChange('all');
  };

  const handleCancel = () => {
    setPendingConfirm(false);
  };

  // Visual state of the "All slides" button — highlighted when active OR pending
  const allActive = scope === 'all' || pendingConfirm;

  /* ── LED variant ── */
  if (variant === 'led') {
    return (
      <div className="relative">
        {/* LED scope indicators — horizontal */}
        <div className="scope-led-row">
          <div
            className="scope-led-item"
            onClick={() => {
              setPendingConfirm(false);
              onScopeChange('slide');
            }}
          >
            <div
              className={cn(
                'scope-led-dot',
                scope === 'slide' && !pendingConfirm && 'scope-led-dot-on'
              )}
              style={scope === 'slide' && !pendingConfirm ? {
                background: '#34d399',
                boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.35), 0 0 6px rgba(52, 211, 153, 0.5)',
              } : undefined}
            />
            <span className={cn(
              'scope-led-label',
              scope === 'slide' && !pendingConfirm && 'scope-led-label-active'
            )}>
              This Slide
            </span>
          </div>
          <div
            className="scope-led-item"
            onClick={handleAllClick}
          >
            <div
              className={cn(
                'scope-led-dot',
                allActive && 'scope-led-dot-on',
                scope === 'all' && !pendingConfirm && 'scope-led-blink'
              )}
              style={allActive ? {
                background: '#e8b84b',
                boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.35), 0 0 6px rgba(232, 184, 75, 0.5)',
              } : undefined}
            />
            <span className={cn(
              'scope-led-label',
              allActive && 'scope-led-label-active'
            )}>
              All Slides
            </span>
          </div>
        </div>

        {/* Inline confirmation — absolute overlay, no layout shift */}
        {pendingConfirm && hasDiversity && (
          <div className="absolute top-full right-0 mt-2 z-20 w-[260px] bg-[#1a1810] border border-amber-500/15 rounded-lg px-2.5 py-2.5 shadow-lg shadow-black/40">
            <div className="flex items-start gap-2 text-[11px] text-amber-400/80">
              <svg className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>
                {confirmMessage || `Slides use ${uniqueCount} different ${diversityNoun}. Apply current selection to all slides?`}
              </span>
            </div>
            <div className="flex gap-2 mt-2.5 ml-5">
              <button
                onClick={handleConfirm}
                className="px-3 py-1 text-[11px] font-medium rounded-md bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors"
              >
                Apply to all
              </button>
              <button
                onClick={handleCancel}
                className="px-3 py-1 text-[11px] font-medium rounded-md bg-white/[0.04] text-white/50 hover:bg-white/[0.08] hover:text-white/70 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Persistent status bar — dismissible, absolute overlay */}
        {scope === 'all' && !pendingConfirm && !statusDismissed && (
          <div className="absolute top-full right-0 mt-2 z-20 flex items-center gap-1.5 text-[11px] text-sky-400/70 bg-[#0f1518] border border-sky-500/10 rounded-lg pl-2.5 pr-1 py-1 shadow-lg shadow-black/30 whitespace-nowrap">
            <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Changes affect all slides</span>
            <button
              onClick={() => setStatusDismissed(true)}
              className="ml-1 p-0.5 rounded text-sky-400/50 hover:text-sky-400/80 transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ── Default segmented variant ── */
  return (
    <div className="mb-3">
      {/* Segmented control */}
      <div className="flex rounded-lg bg-white/[0.04] border border-white/[0.06] p-0.5">
        <button
          onClick={() => {
            setPendingConfirm(false);
            onScopeChange('slide');
          }}
          className={cn(
            'flex-1 text-[11px] font-medium py-1.5 rounded-md transition-all',
            scope === 'slide' && !pendingConfirm
              ? 'bg-white/10 text-white/90 shadow-sm'
              : 'text-white/45 hover:text-white/60'
          )}
        >
          This slide
        </button>
        <button
          onClick={handleAllClick}
          className={cn(
            'flex-1 text-[11px] font-medium py-1.5 rounded-md transition-all',
            allActive
              ? 'bg-white/10 text-white/90 shadow-sm'
              : 'text-white/45 hover:text-white/60'
          )}
        >
          All slides
        </button>
      </div>

      {/* Inline confirmation — shown when "All slides" clicked with diversity */}
      {pendingConfirm && hasDiversity && (
        <div className="mt-2 bg-amber-500/[0.06] border border-amber-500/10 rounded-lg px-2.5 py-2.5">
          <div className="flex items-start gap-2 text-[11px] text-amber-400/80">
            <svg className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>
              {confirmMessage || `Slides use ${uniqueCount} different ${diversityNoun}. Apply current selection to all slides?`}
            </span>
          </div>
          <div className="flex gap-2 mt-2.5 ml-5">
            <button
              onClick={handleConfirm}
              className="px-3 py-1 text-[11px] font-medium rounded-md bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors"
            >
              Apply to all
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-1 text-[11px] font-medium rounded-md bg-white/[0.04] text-white/50 hover:bg-white/[0.08] hover:text-white/70 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Persistent status bar — always visible while in "All slides" mode */}
      {scope === 'all' && !pendingConfirm && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-sky-400/70 bg-sky-500/[0.05] border border-sky-500/10 rounded-lg px-2.5 py-1.5">
          <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Changes affect all slides</span>
        </div>
      )}
    </div>
  );
}
